'use client';

import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';

interface DropdownMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: DropdownMenuItem[];
  align?: 'start' | 'center' | 'end';
}

export function DropdownMenu({
  trigger,
  items,
  align = 'end',
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <DropdownMenuPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuPrimitive.Trigger asChild>
        {trigger}
      </DropdownMenuPrimitive.Trigger>

      <AnimatePresence>
        {isOpen && (
          <DropdownMenuPrimitive.Portal>
            <DropdownMenuPrimitive.Content
              align={align}
              sideOffset={4}
              asChild
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.1 }}
                className={cn(
                  'z-50 min-w-[160px] rounded-xl bg-white p-1 shadow-lg shadow-zinc-900/10',
                  'border border-zinc-200/50'
                )}
              >
                {items.map((item, index) => (
                  <DropdownMenuPrimitive.Item
                    key={index}
                    disabled={item.disabled}
                    onClick={() => {
                      item.onClick();
                      setIsOpen(false);
                    }}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-lg px-3 py-2 text-sm outline-none',
                      'transition-colors focus:bg-zinc-50',
                      item.danger
                        ? 'text-red-600 hover:bg-red-50 focus:bg-red-50'
                        : 'text-zinc-700 hover:bg-zinc-50',
                      item.disabled && 'pointer-events-none opacity-50'
                    )}
                  >
                    {item.label}
                  </DropdownMenuPrimitive.Item>
                ))}
              </motion.div>
            </DropdownMenuPrimitive.Content>
          </DropdownMenuPrimitive.Portal>
        )}
      </AnimatePresence>
    </DropdownMenuPrimitive.Root>
  );
}
